/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { activeResourceAtom } from "@app/resources/atom";
import { useResourceItem } from "@app/resources/hooks";
import { getLockedShortLabel } from "@app/resources/utils/resource-view-limit";
import { type Resource } from "@core/shared/types";
import { formatDuration } from "@core/shared/utils";
import { LockIcon } from "@design-system/components/Icons";
import { cn } from "@design-system/utils";
import { ClockIcon } from "lucide-react";

type ResourceItemProps = {
  resource: Resource;
  className?: string;
  isLinkedResource?: boolean;
};

export function ResourcesGridItem({
  resource,
  className,
  isLinkedResource = false,
}: ResourceItemProps) {
  const {
    handleCardClick,
    theme,
    hasImage,
    title,
    type,
    duration,
    isLocked,
    image,
    getResourceIcon,
    getResourceTypeLabel,
  } = useResourceItem(resource);

  return (
    <div
      className={cn("group h-full", className)}
      onClick={() => {
        const activeResource = document.getElementById("active-resource");

        activeResourceAtom.update(resource);

        if (activeResource) {
          activeResource.scrollIntoView({ behavior: "smooth" });
        }
      }}
    >
      <div
        className={cn(
          "relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border transition-all duration-300",
        )}
        onClick={handleCardClick}
      >
        <div className="pointer-events-none absolute bottom-0 start-0 z-20 h-24 w-full bg-gradient-to-t from-black/75 via-black/30 to-transparent"></div>
        {/* Image/Thumbnail Section */}
        <div className="relative aspect-[4/5] w-full overflow-hidden">
          <div
            className={cn(
              "absolute inset-0 z-10 h-full w-full",
              isLocked ? "bg-black/25" : "bg-black/10",
            )}
          ></div>
          {hasImage ? (
            <>
              <img
                src={image.url}
                alt={title || "Resource image"}
                className="h-full w-full transform object-cover transition duration-500 hover:scale-110 hover:transform hover:transition-transform"
              />
            </>
          ) : (
            <div
              className={cn(
                "flex h-full w-full items-center justify-center",
                `bg-gradient-to-br ${theme.lightGradient}`,
              )}
            >
              <div
                className={cn(
                  "flex h-16 w-16 items-center justify-center rounded-2xl",
                  `bg-gradient-to-r ${theme.gradient} text-white shadow-lg`,
                )}
              >
                {getResourceIcon(type)}
              </div>
            </div>
          )}

          {/* Top Badges */}
          <div className="absolute left-3 right-3 top-3 flex items-center justify-between">
            {/* Type Badge */}
            <div
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold backdrop-blur-md",

                `${theme.iconBg} text-white`,
              )}
            >
              {getResourceIcon(type)}
              <span className="max-w-[80px] truncate">
                {getResourceTypeLabel(type)}
              </span>
            </div>

            {/* Duration */}
            {duration && (
              <div className="flex items-center gap-1 rounded-full bg-black/80 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-md">
                <ClockIcon className="h-3 w-3" />
                {formatDuration(duration)}
              </div>
            )}
          </div>

          {/* Lock Icon Overlay */}
          {isLocked && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[1px]">
              <LockIcon className="h-8 w-8 text-white" />
            </div>
          )}
        </div>

        {/* Content Section - Fixed Height */}
        <div className="absolute bottom-0 z-30 flex flex-1 flex-col p-4">
          <h3
            className={cn(
              "mb-2 line-clamp-2 text-base font-bold leading-tight text-white",
            )}
            style={{
              textShadow: "2px 1.3px 2px #000",
            }}
          >
            {title || `${getResourceTypeLabel(type)} File`}
          </h3>

          {/* Locked Status */}
          {isLocked && (
            <div className="mt-auto flex items-center gap-2 text-xs font-medium text-orange-600 dark:text-orange-400">
              <LockIcon className="h-3 w-3" />
              <span>{getLockedShortLabel(resource)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
